import type { ReactNode } from 'react';

interface LineChartProps {
  data: Array<{ label: string; value: number; secondaryValue?: number }>;
  primaryColor?: string;
  secondaryColor?: string;
  primaryLabel?: string;
  secondaryLabel?: string;
  formatValue?: (value: number) => string;
  height?: number;
}

export function LineChart({
  data,
  primaryColor = '#3b82f6',
  secondaryColor = '#8b5cf6',
  primaryLabel = 'Primary',
  secondaryLabel = 'Secondary',
  formatValue = (v) => v.toLocaleString(),
  height = 200,
}: LineChartProps): ReactNode {
  if (data.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-gray-500">
        No data available
      </div>
    );
  }

  const maxValue = Math.max(
    ...data.map((d) => Math.max(d.value, d.secondaryValue ?? 0))
  );
  const padding = 40;
  const chartWidth = 600;
  const chartHeight = height;
  const hasSecondary = data.some((d) => d.secondaryValue !== undefined);

  // Calculate points for line
  const pointSpacing = (chartWidth - padding * 2) / Math.max(1, data.length - 1);
  const points = data.map((d, i) => ({
    x: padding + i * pointSpacing,
    y: chartHeight - (d.value / maxValue) * (chartHeight - padding * 2) - padding,
    value: d.value,
  }));

  const secondaryPoints = hasSecondary
    ? data.map((d, i) => ({
        x: padding + i * pointSpacing,
        y: chartHeight - ((d.secondaryValue ?? 0) / maxValue) * (chartHeight - padding * 2) - padding,
        value: d.secondaryValue ?? 0,
      }))
    : [];

  // Create path for line
  const createPath = (pts: typeof points): string => {
    if (pts.length === 0) return '';
    if (pts.length === 1) return `M ${pts[0]?.x} ${pts[0]?.y}`;
    
    let path = `M ${pts[0]?.x} ${pts[0]?.y}`;
    for (let i = 1; i < pts.length; i++) {
      const p = pts[i];
      if (p) {
        path += ` L ${p.x} ${p.y}`;
      }
    }
    return path;
  };

  // Create area path (for gradient fill)
  const createAreaPath = (pts: typeof points): string => {
    if (pts.length === 0) return '';
    const path = createPath(pts);
    const last = pts[pts.length - 1];
    const first = pts[0];
    if (last && first) {
      return `${path} L ${last.x} ${chartHeight - padding} L ${first.x} ${chartHeight - padding} Z`;
    }
    return path;
  };

  return (
    <div className="relative h-full w-full">
      {/* Legend */}
      <div className="absolute right-0 top-0 flex items-center gap-4 text-xs">
        <div className="flex items-center gap-1">
          <div
            className="h-3 w-3 rounded"
            style={{ backgroundColor: primaryColor }}
          />
          <span className="text-gray-600">{primaryLabel}</span>
        </div>
        {hasSecondary && (
          <div className="flex items-center gap-1">
            <div
              className="h-3 w-3 rounded"
              style={{ backgroundColor: secondaryColor }}
            />
            <span className="text-gray-600">{secondaryLabel}</span>
          </div>
        )}
      </div>

      <svg
        viewBox={`0 0 ${chartWidth} ${chartHeight + 40}`}
        className="h-full w-full"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((fraction) => {
          const y = chartHeight - fraction * (chartHeight - padding * 2) - padding;
          return (
            <line
              key={fraction}
              x1={padding}
              y1={y}
              x2={chartWidth - padding}
              y2={y}
              stroke="#e5e7eb"
              strokeDasharray={fraction === 0 ? undefined : '4'}
            />
          );
        })}

        {/* Y-axis labels */}
        {[0, 0.25, 0.5, 0.75, 1].map((fraction) => {
          const y = chartHeight - fraction * (chartHeight - padding * 2) - padding;
          const value = maxValue * fraction;
          return (
            <text
              key={fraction}
              x={padding - 5}
              y={y + 4}
              textAnchor="end"
              className="fill-gray-400 text-[10px]"
            >
              {formatValue(value)}
            </text>
          );
        })}

        {/* Area fill (gradient) */}
        <defs>
          <linearGradient id="primaryGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={primaryColor} stopOpacity="0.3" />
            <stop offset="100%" stopColor={primaryColor} stopOpacity="0" />
          </linearGradient>
          {hasSecondary && (
            <linearGradient id="secondaryGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={secondaryColor} stopOpacity="0.3" />
              <stop offset="100%" stopColor={secondaryColor} stopOpacity="0" />
            </linearGradient>
          )}
        </defs>

        {/* Primary area */}
        <path
          d={createAreaPath(points)}
          fill="url(#primaryGradient)"
        />

        {/* Secondary area */}
        {hasSecondary && (
          <path
            d={createAreaPath(secondaryPoints)}
            fill="url(#secondaryGradient)"
          />
        )}

        {/* Primary line */}
        <path
          d={createPath(points)}
          fill="none"
          stroke={primaryColor}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Secondary line */}
        {hasSecondary && (
          <path
            d={createPath(secondaryPoints)}
            fill="none"
            stroke={secondaryColor}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray="4 4"
          />
        )}

        {/* Data points */}
        {points.map((point, i) => (
          <g key={i}>
            <circle
              cx={point.x}
              cy={point.y}
              r="4"
              fill={primaryColor}
              stroke="white"
              strokeWidth="2"
            />
            <title>{formatValue(point.value)}</title>
          </g>
        ))}

        {hasSecondary && secondaryPoints.map((point, i) => (
          <g key={`secondary-${i}`}>
            <circle
              cx={point.x}
              cy={point.y}
              r="4"
              fill={secondaryColor}
              stroke="white"
              strokeWidth="2"
            />
            <title>{formatValue(point.value)}</title>
          </g>
        ))}

        {/* X-axis labels */}
        {data.map((d, i) => {
          const x = padding + i * pointSpacing;
          return (
            <text
              key={i}
              x={x}
              y={chartHeight + 20}
              textAnchor="middle"
              className="fill-gray-500 text-[9px]"
            >
              {d.label}
            </text>
          );
        })}
      </svg>
    </div>
  );
}
